import React from 'react';
import styled, { css } from 'styled-components';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const getVariantStyles = (variant: string) => {
  switch (variant) {
    case 'primary':
      return css`
        background: linear-gradient(135deg, ${({ theme }) => theme.colors.navyBlue}, ${({ theme }) => theme.colors.lightNavy});
        color: white;
        border: 1px solid ${({ theme }) => theme.colors.navyBlue};

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, ${({ theme }) => theme.colors.lightNavy}, ${({ theme }) => theme.colors.navyBlue});
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3);
        }
        
        /* Touch feedback for mobile */
        @media (hover: none) and (pointer: coarse) {
          &:active:not(:disabled) {
            background: ${({ theme }) => theme.colors.lightNavy};
            transform: scale(0.98);
            box-shadow: 0 4px 15px rgba(30, 58, 138, 0.4);
          }
          
          &:hover {
            transform: none; /* Disable hover transform on touch devices */
            box-shadow: none;
          }
        }
      `;
    case 'secondary':
      return css`
        background: ${({ theme }) => theme.colors.surface};
        color: ${({ theme }) => theme.colors.textPrimary};
        border: 1px solid ${({ theme }) => theme.colors.border};

        &:hover:not(:disabled) {
          background: ${({ theme }) => theme.colors.background};
          border-color: ${({ theme }) => theme.colors.navyBlue};
          transform: translateY(-2px);
        }
        
        /* Touch feedback for mobile */
        @media (hover: none) and (pointer: coarse) {
          &:active:not(:disabled) {
            background: ${({ theme }) => theme.colors.background};
            border-color: ${({ theme }) => theme.colors.navyBlue};
            transform: scale(0.98);
          }
          
          &:hover {
            transform: none;
          }
        }
      `;
    case 'outline':
      return css`
        background: transparent;
        color: ${({ theme }) => theme.colors.navyBlue};
        border: 1px solid ${({ theme }) => theme.colors.navyBlue};

        &:hover:not(:disabled) {
          background: rgba(30, 58, 138, 0.1);
          transform: translateY(-2px);
        }
        
        /* Touch feedback for mobile */
        @media (hover: none) and (pointer: coarse) {
          &:active:not(:disabled) {
            background: rgba(30, 58, 138, 0.2);
            transform: scale(0.98);
          }
          
          &:hover {
            transform: none;
          }
        }
      `;
    default:
      return '';
  }
};

const getSizeStyles = (size: string) => {
  switch (size) {
    case 'sm':
      return css`
        padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
        font-size: ${({ theme }) => theme.fontSizes.sm};
        min-height: 36px;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
          min-height: 44px; /* Ensure minimum touch target on mobile */
          padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
        }
      `;
    case 'md':
      return css`
        padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
        font-size: ${({ theme }) => theme.fontSizes.base};
        min-height: 44px;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
          min-height: 48px;
          padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
        }
      `;
    case 'lg':
      return css`
        padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
        font-size: ${({ theme }) => theme.fontSizes.lg};
        min-height: 52px;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
          min-height: 56px;
          padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing['2xl']};
          font-size: ${({ theme }) => theme.fontSizes.base}; /* Slightly smaller on mobile */
        }
      `;
    default:
      return '';
  }
};

const StyledButton = styled.button<Omit<ButtonProps, 'loading'> & { $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  text-decoration: none;
  position: relative;
  overflow: hidden;
  
  /* Improve mobile performance */
  will-change: transform;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  
  /* Better mobile touch targets */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border-radius: ${({ theme }) => theme.borderRadius.lg}; /* Larger radius on mobile */
  }

  ${({ variant = 'primary' }) => getVariantStyles(variant)}
  ${({ size = 'md' }) => getSizeStyles(size)}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    
    &:hover {
      transform: none !important;
      box-shadow: none !important;
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.navyBlue};
    outline-offset: 2px;
  }

  /* Loading state */
  ${({ $loading }) =>
    $loading &&
    css`
      pointer-events: none;
      
      &::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        border: 2px solid transparent;
        border-top: 2px solid currentColor;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        
        @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
          width: 18px;
          height: 18px;
        }
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `}
`;

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  className,
  ...props
}) => {
  return (
    <StyledButton
      variant={variant}
      size={size}
      $loading={loading}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      className={className}
      {...props}
    >
      {loading ? '' : children}
    </StyledButton>
  );
}; 